export type Language = "fr" | "en"

export const translations = {
  fr: {
    nav: {
      services: "Services",
      process: "Processus",
      portfolio: "Réalisations",
      testimonials: "Témoignages",
      faq: "FAQ",
      cta: "On avance ensemble ?",
    },
    hero: {
      badge: "Pour les fondateurs SaaS en quête de croissance",
      preHighlight1: "On",
      highlight1: "développe",
      postHighlight1: "votre SaaS",
      preHighlight2: "et on trouve",
      highlight2: "vos premiers clients",
      subtitle: "Une approche sur-mesure, un accompagnement humain. On vous guide dans le lancement de votre produit jusqu'au sommet.",
      ctaPrimary: "Commencer l'ascension",
      ctaSecondary: "Découvrir notre approche",
    },
    stats: {
      projects: "projets lancés",
      timeline: "semaines de dev",
      satisfaction: "clients satisfaits",
    },
    services: {
      eyebrow: "Ce qu'on fait",
      title: "Tout ce qu'il faut pour lancer et grandir",
      subtitle: "On ne fait pas que coder. On vous accompagne de A à Z, du développement jusqu'à vos premiers revenus.",
      devLabel: "Développement",
      growthLabel: "Croissance",
      dev: {
        saas: { title: "Développement SaaS", desc: "Architecture moderne, code maintenable, technologies éprouvées. On construit sur des fondations solides." },
        mvp: { title: "MVP Rapide", desc: "De l'idée au produit en 2-4 semaines. On valide vite pour itérer vite." },
        cloud: { title: "Infrastructure Cloud", desc: "Déploiement, monitoring, scaling. Votre app reste performante quand vous grandissez." },
      },
      growth: {
        prospecting: { title: "Pipeline de Prospection", desc: "Stratégie d'acquisition, séquences d'outreach, automation. On vous aide à décrocher vos premiers clients." },
        automation: { title: "Automatisation", desc: "Workflows intelligents, intégrations API, processus optimisés pour vous faire gagner du temps." },
        growthEng: { title: "Growth Engineering", desc: "Analytics, A/B testing, optimisation des conversions. On mesure tout pour améliorer tout." },
      },
    },
    process: {
      eyebrow: "Notre approche",
      title: "Un chemin clair vers le sommet",
      subtitle: "Comme un guide de montagne, on connaît le terrain. On vous accompagne étape par étape, sans mauvaise surprise.",
      steps: {
        discovery: { title: "Échange & Découverte", desc: "On discute de votre vision, vos objectifs, votre marché. On comprend avant de proposer.", duration: "1-2 jours" },
        strategy: { title: "Stratégie & Cadrage", desc: "On définit le scope, les priorités, l'architecture. Un plan clair avant de coder.", duration: "2-3 jours" },
        dev: { title: "Développement", desc: "Sprints courts, démos régulières, feedback continu. Vous voyez avancer chaque semaine.", duration: "2-4 semaines" },
        launch: { title: "Lancement", desc: "Déploiement, tests, mise en production. Votre SaaS est live et prêt à accueillir des utilisateurs.", duration: "2-3 jours" },
        acquisition: { title: "Acquisition", desc: "On met en place votre pipeline de prospection. Premiers contacts, premiers leads.", duration: "1-2 semaines" },
        iteration: { title: "Itération", desc: "On analyse, on optimise, on améliore. Votre produit évolue avec vos premiers retours.", duration: "Continu" },
      },
    },
    portfolio: {
      eyebrow: "Réalisations",
      title: "Des sommets déjà atteints",
      subtitle: "Chaque projet est une nouvelle ascension. Voici quelques-unes de nos expéditions réussies.",
      metricLabel: "Résultat",
      projects: {
        prospectflow: {
          category: "B2B Automation",
          description: "Pipeline de prospection automatisé pour PME genevoises. Enrichissement intelligent de bases de données entreprises, intégration CRM et séquences d'outreach personnalisées.",
          results: "56 000 entreprises genevoises enrichies",
        },
        cheezeops: {
          category: "Healthtech",
          description: "Plateforme interne de gestion opérationnelle pour réseau de cliniques dentaires. Centralisation des workflows, reporting en temps réel et automatisation des processus métier.",
          results: "7 cliniques & 45 praticiens sous gestion",
        },
        b2bswiss: {
          category: "Legaltech",
          description: "Plateforme SaaS guidant les expats à travers le processus de naturalisation suisse. Roadmap personnalisée, gestion documentaire et suivi administratif OCPM.",
          results: "5 expats onboardés avant sortie de la v1",
        },
        lensmentor: {
          category: "Community Platform",
          description: "Plateforme communautaire pour photographes amateurs. Partage de portfolios, critiques constructives et apprentissage collaboratif entre passionnés de photographie.",
          results: "Assistant IA photographe avec 8 modes d'analyse",
        },
        flashposter: {
          category: "Martech",
          description: "Outil d'intelligence artificielle pour création de posts de blog. De l'inspiration à la publication en trois clics, avec optimisation SEO et génération de contenu assistée.",
          results: "2 500 articles publiés sur WordPress",
        },
        lcde: {
          category: "Community Platform",
          description: "Refonte complète d'une plateforme communautaire pour entrepreneurs français. Migration WordPress vers architecture moderne avec amélioration des performances et de l'expérience utilisateur.",
          results: "50 entrepreneurs actifs quotidiennement",
        },
        lemonads: {
          category: "Martech",
          description: "Système de scraping avancé pour agence marketing genevoise. Extraction automatisée de liens d'affiliation et emails depuis listes de sites web pour campagnes d'outreach personnalisées.",
          results: "12 000 emails envoyés",
        },
      },
    },
    testimonials: {
      eyebrow: "Témoignages",
      title: "Ce que disent nos compagnons de cordée",
      subtitle: "Chaque collaboration est une aventure partagée. Voici les retours de ceux qui ont fait le chemin avec nous.",
    },
    founder: {
      eyebrow: "Pourquoi j'ai créé LémanIT",
      quote: "Un bon produit sans clients ne vaut rien. La plupart des fondateurs SaaS passent des mois à créer un MVP, puis découvrent la prospection. C'est trop tard. Léman IT fait produit et distribution en parallèle. Ici, on parle code et MRR.",
      name: "Thibault",
      role: "Fondateur",
    },
    faq: {
      eyebrow: "Questions fréquentes",
      title: "Tout ce que vous voulez savoir",
      subtitle: "Des questions avant de se lancer ? On répond aux plus courantes ici.",
      items: {
        cost: { q: "Combien coûte le développement d'un SaaS ?", a: "Ça dépend de la complexité, mais on est transparents dès le départ. Un MVP simple démarre autour de 5-15K€, un produit plus complet entre 20-40K€. On établit un devis détaillé après notre premier échange, sans surprise." },
        timeline: { q: "Quels sont vos délais de livraison ?", a: "Un MVP fonctionnel en 2-4 semaines, c'est notre standard. Pour des projets plus ambitieux, comptez 6-12 semaines. On travaille en sprints courts avec des démos régulières, donc vous voyez avancer chaque semaine." },
        pipeline: { q: "Comment fonctionne le pipeline de prospection ?", a: "On vous aide à définir votre ICP (client idéal), on construit vos listes de prospects, on rédige vos séquences d'outreach, et on met en place l'automation. Vous repartez avec un système qui tourne, pas juste des conseils." },
        stack: { q: "Quelles technologies utilisez-vous ?", a: "On privilégie des stacks modernes et éprouvées : Next.js, React, Node.js, PostgreSQL, Supabase, Vercel. On choisit les outils adaptés à votre projet, pas le dernier framework à la mode." },
        maintenance: { q: "Proposez-vous de la maintenance après le lancement ?", a: "Oui, on propose des forfaits de maintenance et d'évolution. Corrections de bugs, nouvelles features, optimisations. On reste à vos côtés pour faire grandir votre produit." },
        collaboration: { q: "Comment se passe la collaboration au quotidien ?", a: "Communication fluide via Clickup, démos hebdomadaires, prévisualisation en temps réel de la plateforme de développement. On est réactifs et disponibles. Vous n'êtes jamais dans le flou sur l'avancement." },
      },
    },
    contact: {
      eyebrow: "Contact",
      title: "Prêt à démarrer l'ascension ?",
      subtitle: "Parlez-nous de votre projet. Premier échange gratuit et sans engagement.",
      form: {
        firstName: "Prénom",
        lastName: "Nom",
        email: "Email",
        company: "Entreprise (optionnel)",
        budget: "Budget estimé",
        budgetPlaceholder: "Sélectionnez une fourchette",
        budgetOptions: {
          "5-10k": "5K - 10K €",
          "10-20k": "10K - 20K €",
          "20-40k": "20K - 40K €",
          "40k+": "40K+ €",
        },
        message: "Parlez-nous de votre projet",
        messagePlaceholder: "Décrivez votre idée, vos objectifs, vos contraintes...",
        submit: "Envoyer le message",
        submitting: "Envoi en cours...",
        success: "Message envoyé !",
        successDesc: "On vous recontacte rapidement.",
        error: "Erreur",
        errorDesc: "Une erreur est survenue. Réessayez.",
      },
      direct: {
        title: "Ou contactez-nous directement",
        email: "Email",
        phone: "Téléphone",
      },
      call: {
        title: "Préférez un appel ?",
        desc: "Réservez un créneau de 30 min pour discuter de votre projet. C'est gratuit et sans engagement.",
        cta: "Réserver un appel",
      },
    },
    merci: {
      title: "Message bien reçu !",
      subtitle: "Merci pour votre intérêt. Je reviens vers vous sous 24h pour discuter de votre projet SaaS.",
      countdown: "Redirection automatique dans",
      seconds: "seconde",
      secondsPlural: "secondes",
      cta: "Retour à l'accueil",
    },
    footer: {
      tagline: "avec ♥ au bord du Lac",
    },
    lang: {
      switchTo: "Switch to English",
      current: "FR",
    },
  },
  en: {
    nav: {
      services: "Services",
      process: "Process",
      portfolio: "Work",
      testimonials: "Testimonials",
      faq: "FAQ",
      cta: "Let's move forward?",
    },
    hero: {
      badge: "For SaaS founders seeking growth",
      preHighlight1: "We",
      highlight1: "build",
      postHighlight1: "your SaaS",
      preHighlight2: "and find",
      highlight2: "your first customers",
      subtitle: "A tailored approach, human support. We guide you from product launch to the summit.",
      ctaPrimary: "Start the ascent",
      ctaSecondary: "Discover our approach",
    },
    stats: {
      projects: "projects launched",
      timeline: "weeks to ship",
      satisfaction: "happy clients",
    },
    services: {
      eyebrow: "What we do",
      title: "Everything you need to launch and grow",
      subtitle: "We don't just code. We guide you from A to Z, from development to your first revenue.",
      devLabel: "Development",
      growthLabel: "Growth",
      dev: {
        saas: { title: "SaaS Development", desc: "Modern architecture, maintainable code, proven technologies. We build on solid foundations." },
        mvp: { title: "Fast MVP", desc: "From idea to product in 2-4 weeks. We validate fast to iterate fast." },
        cloud: { title: "Cloud Infrastructure", desc: "Deployment, monitoring, scaling. Your app stays performant as you grow." },
      },
      growth: {
        prospecting: { title: "Prospecting Pipeline", desc: "Acquisition strategy, outreach sequences, automation. We help you land your first customers." },
        automation: { title: "Automation", desc: "Smart workflows, API integrations, optimized processes to save you time." },
        growthEng: { title: "Growth Engineering", desc: "Analytics, A/B testing, conversion optimization. We measure everything to improve everything." },
      },
    },
    process: {
      eyebrow: "Our approach",
      title: "A clear path to the summit",
      subtitle: "Like a mountain guide, we know the terrain. We accompany you step by step, with no surprises.",
      steps: {
        discovery: { title: "Discovery & Exchange", desc: "We discuss your vision, goals, and market. We understand before we propose.", duration: "1-2 days" },
        strategy: { title: "Strategy & Scoping", desc: "We define scope, priorities, and architecture. A clear plan before writing code.", duration: "2-3 days" },
        dev: { title: "Development", desc: "Short sprints, regular demos, continuous feedback. You see progress every week.", duration: "2-4 weeks" },
        launch: { title: "Launch", desc: "Deployment, testing, production. Your SaaS is live and ready for users.", duration: "2-3 days" },
        acquisition: { title: "Acquisition", desc: "We set up your prospecting pipeline. First contacts, first leads.", duration: "1-2 weeks" },
        iteration: { title: "Iteration", desc: "We analyze, optimize, and improve. Your product evolves with your first feedback.", duration: "Ongoing" },
      },
    },
    portfolio: {
      eyebrow: "Work",
      title: "Summits already reached",
      subtitle: "Every project is a new ascent. Here are some of our successful expeditions.",
      metricLabel: "Result",
      projects: {
        prospectflow: {
          category: "B2B Automation",
          description: "Automated prospecting pipeline for Geneva SMEs. Intelligent enrichment of company databases, CRM integration and personalized outreach sequences.",
          results: "56,000 Geneva companies enriched",
        },
        cheezeops: {
          category: "Healthtech",
          description: "Internal operational management platform for a dental clinic network. Centralized workflows, real-time reporting and business process automation.",
          results: "7 clinics & 45 practitioners managed",
        },
        b2bswiss: {
          category: "Legaltech",
          description: "SaaS platform guiding expats through the Swiss naturalization process. Personalized roadmap, document management and OCPM administrative tracking.",
          results: "5 expats onboarded before v1 release",
        },
        lensmentor: {
          category: "Community Platform",
          description: "Community platform for amateur photographers. Portfolio sharing, constructive critiques and collaborative learning among photography enthusiasts.",
          results: "AI photography assistant with 8 analysis modes",
        },
        flashposter: {
          category: "Martech",
          description: "AI tool for creating blog posts. From inspiration to publication in three clicks, with SEO optimization and assisted content generation.",
          results: "2,500 articles published on WordPress",
        },
        lcde: {
          category: "Community Platform",
          description: "Complete redesign of a community platform for French entrepreneurs. WordPress migration to modern architecture with improved performance and user experience.",
          results: "50 active entrepreneurs daily",
        },
        lemonads: {
          category: "Martech",
          description: "Advanced scraping system for a Geneva marketing agency. Automated extraction of affiliate links and emails from website lists for personalized outreach campaigns.",
          results: "12,000 emails sent",
        },
      },
    },
    testimonials: {
      eyebrow: "Testimonials",
      title: "What our rope partners say",
      subtitle: "Every collaboration is a shared adventure. Here is feedback from those who climbed with us.",
    },
    founder: {
      eyebrow: "Why I created LémanIT",
      quote: "A good product without customers is worthless. Most SaaS founders spend months building an MVP, then discover prospecting. It's too late. Léman IT does product and distribution in parallel. Here, we talk code and MRR.",
      name: "Thibault",
      role: "Founder",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Everything you want to know",
      subtitle: "Questions before getting started? We answer the most common ones here.",
      items: {
        cost: { q: "How much does SaaS development cost?", a: "It depends on complexity, but we're transparent from the start. A simple MVP starts around 5-15K€, a more complete product between 20-40K€. We provide a detailed quote after our first call, no surprises." },
        timeline: { q: "What are your delivery timelines?", a: "A functional MVP in 2-4 weeks is our standard. For more ambitious projects, expect 6-12 weeks. We work in short sprints with regular demos, so you see progress every week." },
        pipeline: { q: "How does the prospecting pipeline work?", a: "We help you define your ICP (ideal customer), build prospect lists, write outreach sequences, and set up automation. You leave with a running system, not just advice." },
        stack: { q: "What technologies do you use?", a: "We favor modern, proven stacks: Next.js, React, Node.js, PostgreSQL, Supabase, Vercel. We choose tools suited to your project, not the latest trending framework." },
        maintenance: { q: "Do you offer maintenance after launch?", a: "Yes, we offer maintenance and evolution packages. Bug fixes, new features, optimizations. We stay by your side to grow your product." },
        collaboration: { q: "How does daily collaboration work?", a: "Smooth communication via ClickUp, weekly demos, real-time preview of the development platform. We're responsive and available. You're never in the dark about progress." },
      },
    },
    contact: {
      eyebrow: "Contact",
      title: "Ready to start the ascent?",
      subtitle: "Tell us about your project. First call is free and without commitment.",
      form: {
        firstName: "First name",
        lastName: "Last name",
        email: "Email",
        company: "Company (optional)",
        budget: "Estimated budget",
        budgetPlaceholder: "Select a range",
        budgetOptions: {
          "5-10k": "5K - 10K €",
          "10-20k": "10K - 20K €",
          "20-40k": "20K - 40K €",
          "40k+": "40K+ €",
        },
        message: "Tell us about your project",
        messagePlaceholder: "Describe your idea, goals, constraints...",
        submit: "Send message",
        submitting: "Sending...",
        success: "Message sent!",
        successDesc: "We'll get back to you quickly.",
        error: "Error",
        errorDesc: "Something went wrong. Please try again.",
      },
      direct: {
        title: "Or contact us directly",
        email: "Email",
        phone: "Phone",
      },
      call: {
        title: "Prefer a call?",
        desc: "Book a 30-min slot to discuss your project. It's free and without commitment.",
        cta: "Book a call",
      },
    },
    merci: {
      title: "Message received!",
      subtitle: "Thank you for your interest. I'll get back to you within 24h to discuss your SaaS project.",
      countdown: "Automatic redirection in",
      seconds: "second",
      secondsPlural: "seconds",
      cta: "Back to home",
    },
    footer: {
      tagline: "with ♥ by the Lake",
    },
    lang: {
      switchTo: "Passer en français",
      current: "EN",
    },
  },
} as const

export type Translations = typeof translations
export type TranslationKey = keyof Translations["fr"]
